import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

type Props = {
  /** 引数行（argv 順）。各行が 1 引数に対応する */
  lines: string[];
  /** 行の変更ハンドラ */
  onChange: (lines: string[]) => void;
  /** 無効化（実行中・編集不可時など） */
  disabled?: boolean;
};

/**
 * トリム後空でない行だけを取り出す（表示個数・ツールチップ用）。
 * 実際の送信時の整形（空除外）と揃える。
 */
const nonEmpty = (lines: string[]): string[] => lines.map((l) => l.trim()).filter((l) => l !== '');

/**
 * 実行時引数の入力 UI（IntelliJ 風の引数チップ）。
 *
 * - 折りたたみ時は `⚙ 引数 (N)`（N=空でない引数の数）を表示。ホバーで現在の引数一覧をツールチップ表示。
 * - クリックでポップオーバーを開き、引数行を縦に並べる（各行: テキスト入力 + 削除(✕)）。
 * - 下部に「+ 引数を追加」。行の並び順 = argv 順。
 *
 * 値の保持・実行時の整形（空行除外して配列送信）は呼び出し側（WorkspacePage）が行う。
 */
const ArgumentsChip: React.FC<Props> = ({ lines, onChange, disabled = false }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const effective = nonEmpty(lines);
  const count = effective.length;
  const tooltip = count > 0 ? effective.join(' ') : '引数なし';

  const updateLine = (index: number, value: string) => {
    onChange(lines.map((l, i) => (i === index ? value : l)));
  };
  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };
  const addLine = () => {
    onChange([...lines, '']);
  };

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <Chip
          icon={<TuneIcon sx={{ fontSize: 16 }} />}
          label={count > 0 ? `引数 ${count}` : '引数'}
          size="small"
          variant="outlined"
          onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
          disabled={disabled}
          sx={{
            fontWeight: 600,
            borderRadius: 999,
            height: 32,
            px: 0.5,
            borderColor: count > 0 ? 'primary.main' : 'divider',
            color: count > 0 ? 'primary.main' : 'text.secondary',
            bgcolor: open ? 'action.hover' : 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.2s',
            '& .MuiChip-icon': { color: count > 0 ? 'primary.main' : 'text.secondary' },
            '&:hover': { bgcolor: 'action.hover' },
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: { borderRadius: 3, border: 1, borderColor: 'divider', mt: -1.5, width: 460 },
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* ヘッダー: タイトル + 追加アイコン（右上に固定配置） */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <TuneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                実行時引数
              </Typography>
              {count > 0 && (
                <Box
                  sx={{
                    px: 0.75,
                    height: 18,
                    minWidth: 18,
                    borderRadius: 9,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {count}
                </Box>
              )}
            </Stack>
            <Tooltip title="引数を追加" arrow>
              <IconButton size="small" aria-label="引数を追加" onClick={addLine} color="primary">
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ px: 2.5, py: 2 }}>
            {/* 引数行: CLI 風プロンプト + 入力 + 削除。行はカード状で、ホバーで削除が出る */}
            {lines.length === 0 ? (
              <Box
                onClick={addLine}
                sx={{
                  py: 3,
                  borderRadius: 2,
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <AddIcon sx={{ fontSize: 22, color: 'text.secondary', mb: 0.25 }} />
                <Typography variant="body2" color="text.secondary">
                  引数を追加
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {lines.map((line, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      pl: 1.25,
                      pr: 0.5,
                      py: 0.25,
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      border: 1,
                      borderColor: 'transparent',
                      transition: 'border-color 0.2s',
                      '&:focus-within': { borderColor: 'primary.main' },
                      '&:hover .arg-remove': { opacity: 1 },
                    }}
                  >
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        color: 'text.disabled',
                        userSelect: 'none',
                      }}
                    >
                      $
                    </Typography>
                    <TextField
                      size="small"
                      fullWidth
                      variant="standard"
                      autoFocus={index === lines.length - 1}
                      value={line}
                      placeholder={`引数 ${index + 1}`}
                      onChange={(e) => updateLine(index, e.target.value)}
                      slotProps={{
                        input: {
                          disableUnderline: true,
                          sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                        },
                      }}
                    />
                    <Tooltip title="削除" arrow>
                      <IconButton
                        className="arg-remove"
                        size="small"
                        aria-label="引数を削除"
                        onClick={() => removeLine(index)}
                        sx={{
                          flexShrink: 0,
                          opacity: 0,
                          transition: 'opacity 0.2s, color 0.2s',
                          color: 'text.secondary',
                          '&:hover': { color: 'error.main' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}

            {/* コマンドラインプレビュー（等幅・薄背景。引数がある時だけ） */}
            {count > 0 && (
              <Box
                sx={{
                  mt: 1.75,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: '#0d1117',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: '#7d8590', mb: 0.25 }}>
                  コマンドライン引数
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ display: 'block', fontFamily: 'monospace', color: '#e6edf3' }}
                >
                  {effective.join(' ')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ArgumentsChip;
